import { getEnvironment, getEnvironmentValue, ENVIRONMENT_VALUES } from '../../core';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { ENVIRONMENTS } from '../../core/constants/ENVIRONMENTS';

/**
 * @group Functions
 * @summary <blockquote>Gets the base URL for the application based on the current environment.</blockquote>
 * 
 * @returns Result containing the base URL for the application or an AppError if URL determination fails
 * 
 * @example
 * ```typescript
 * import { getAppBaseUrl } from '@shared/utils';
 * 
 * const result = getAppBaseUrl();
 * result.match({
 *   ok: (baseUrl) => console.log(`Base URL: ${baseUrl}`),
 *   err: (error) => console.error(`Failed: ${error.errorMessage}`)
 * });
 * ```
 */

export function getAppBaseUrl(): Result<string, AppError> {
  const isBrowser = typeof window !== 'undefined';

  // Browser environment: Always use window.location.origin for client-side requests
  if (isBrowser) {
    // Ensure we only get the origin (protocol + hostname + port) without path
    const origin = window.location.protocol + '//' + window.location.host;
    return ok(origin);
  }

  // Server environment: Use environment-specific URL resolution
  const environmentResult = getEnvironment();

  return environmentResult.match({
    ok: (environment) => {
      // Determine if NEXT_PUBLIC_URL is required based on environment
      // Production and preview require the environment variable, development allows fallback
      const isRequired = environment === ENVIRONMENTS.PRODUCTION || environment === ENVIRONMENTS.PREVIEW;
      
      // Get the URL from environment variable
      const urlResult = getEnvironmentValue(ENVIRONMENT_VALUES.URL.NEXT_PUBLIC_URL, isRequired);
      
      return urlResult.match({
        ok: (url: string) => ok(url),
        // eslint-disable-next-line @template/no-business-logic-in-error-handling
        err: () => {
          // Handle missing environment variable based on environment
          if (environment === ENVIRONMENTS.DEVELOPMENT) {
            // Development: Fall back to localhost if NEXT_PUBLIC_URL is not set
            return ok('http://localhost:3000');
          }
          
          // Production and preview: Return error if NEXT_PUBLIC_URL is not set
          return err({
            errorCode: 'MISSING_URL',
            errorMessage: `NEXT_PUBLIC_URL environment variable is required for ${environment} environment`
          });
        }
      });
    },
    err: (error) => {
      // Environment detection failed - return error to ensure proper configuration
      return err({
        errorCode: 'ENVIRONMENT_DETECTION_FAILED',
        errorMessage: `Failed to determine base URL due to environment detection error: ${error.errorMessage}`
      });
    }
  });
} 